import graphApiSlice from 'src/shared/state/api-slices/graphApiSlice';
import type { AppDispatch, RootState } from 'src/store';
import type { Role, User } from 'src/shared/types';
import type { UserProjectAccess as FormUserProjectAccess } from './state/userManagementSlice';

type UserListDraft = { users: User[] };

/** graphApi uses injectEndpoints; endpoint names are not inferred on util in this repo's RootState typing. */
const listCacheUtil = graphApiSlice.util as typeof graphApiSlice.util & {
    selectCachedArgsForQuery: (state: RootState, queryName: string) => readonly unknown[];
    updateQueryData: (endpointName: string, args: unknown, updateRecipe: (draft: UserListDraft) => void) => never;
};

function patchCachedUserList(
    dispatch: AppDispatch,
    state: RootState,
    endpointName: 'getCustomerUsers' | 'getCustomerUsersForSuperAdmin',
    update: (draft: UserListDraft) => void
): void {
    const argsList = listCacheUtil.selectCachedArgsForQuery(state, endpointName);
    for (const args of argsList) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        dispatch(listCacheUtil.updateQueryData(endpointName, args, update) as any);
    }
}

/** Merge form projects into the current customer row, preserving projectName from cache. */
function mapFormProjectsToCustomerProjects(user: User, customerId: string, projects: FormUserProjectAccess[]): void {
    const cust = user.customers.find(c => c.customerId === customerId);
    if (!cust) {
        return;
    }
    const prev = cust.projects || [];
    cust.projects = projects.map(p => ({
        projectId: p.projectId,
        projectName: prev.find(pp => pp.projectId === p.projectId)?.projectName ?? p.projectId,
        roles: (p.roles as Role[] | null) ?? null,
    }));
}

/** After a full edit save, update list rows without refetching. */
export function patchManagementListsAfterUserEditSave(
    dispatch: AppDispatch,
    state: RootState,
    params: {
        cognitoId: string;
        customerId: string;
        firstName: string;
        lastName: string;
        phone: string;
        phoneCountryCode: string;
        tnc_consent: boolean;
        roles: Role[];
        projects: FormUserProjectAccess[];
    }
): void {
    const apply = (user: User) => {
        user.firstName = params.firstName.trim();
        user.lastName = params.lastName.trim();
        user.phone = params.phone;
        user.phoneCountryCode = params.phoneCountryCode;
        user.tnc_consent = params.tnc_consent;
        const cust = user.customers.find(c => c.customerId === params.customerId);
        if (cust) {
            cust.roles = params.roles?.length ? [...params.roles] : null;
        }
        mapFormProjectsToCustomerProjects(user, params.customerId, params.projects);
    };

    for (const name of ['getCustomerUsers', 'getCustomerUsersForSuperAdmin'] as const) {
        patchCachedUserList(dispatch, state, name, draft => {
            const idx = draft.users.findIndex(u => u.cognitoId === params.cognitoId);
            if (idx >= 0) {
                apply(draft.users[idx]);
            }
        });
    }
}

/** After a column bulk or other project-only patch, update list rows. */
export function patchManagementListsAfterProjectSync(
    dispatch: AppDispatch,
    state: RootState,
    params: { cognitoId: string; customerId: string; projects: FormUserProjectAccess[] }
): void {
    for (const name of ['getCustomerUsers', 'getCustomerUsersForSuperAdmin'] as const) {
        patchCachedUserList(dispatch, state, name, draft => {
            const idx = draft.users.findIndex(u => u.cognitoId === params.cognitoId);
            if (idx >= 0) {
                mapFormProjectsToCustomerProjects(draft.users[idx], params.customerId, params.projects);
            }
        });
    }
}
